import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/App';
import { Pencil, Trash2, Plus, X, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ALL_PERMISSIONS = [
  { key: 'site_settings', label: 'Site Ayarları' },
  { key: 'mail_settings', label: 'Mail Ayarları' },
  { key: 'users', label: 'Kullanıcı Yönetimi' },
  { key: 'technical_docs', label: 'AI Bilgi Bankası' },
  { key: 'file_share', label: 'Dosya Paylaşımı' },
  { key: 'payment_links', label: 'Ödeme Linkleri' },
  { key: 'services', label: 'Hizmetler' },
  { key: 'projects', label: 'Projeler' },
  { key: 'blog', label: 'Blog' },
  { key: 'certificates', label: 'Sertifikalar' },
  { key: 'jobs', label: 'Kariyer İlanları' },
  { key: 'applications', label: 'Başvurular' },
  { key: 'faq', label: 'SSS' },
  { key: 'messages', label: 'Mesajlar' },
  { key: 'quotes', label: 'Teklifler' },
];

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    permissions: []
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', permissions: ALL_PERMISSIONS.map(p => p.key) });
    setShowPassword(false);
    setShowPermissions(false);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      permissions: user.permissions || ALL_PERMISSIONS.map(p => p.key)
    });
    setShowPassword(false);
    setShowPermissions(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', permissions: [] });
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const toggleAllPermissions = () => {
    const allKeys = ALL_PERMISSIONS.map(p => p.key);
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.length === allKeys.length ? [] : allKeys
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast.error('Ad ve e-posta zorunludur');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Yeni kullanıcı için şifre zorunludur');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('admin_token');

    try {
      if (editingUser) {
        const body = {
          name: formData.name,
          email: formData.email,
          permissions: formData.permissions
        };
        if (formData.password) {
          body.password = formData.password;
        }
        await axios.put(`${BACKEND_URL}/api/admin/users/${editingUser.id}`, body, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kullanıcı güncellendi');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/users`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          permissions: formData.permissions
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kullanıcı oluşturuldu');
      }
      closeModal();
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'İşlem başarısız';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`"${userName}" kullanıcısını silmek istediğinize emin misiniz?`)) {
      return;
    }

    setDeletingId(userId);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Silme işlemi başarısız';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionCount = (user) => {
    const perms = user.permissions || [];
    return perms.length;
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Kullanıcı Yönetimi</h1>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Yeni Kullanıcı
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            Henüz kullanıcı bulunmuyor.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Ad</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">E-posta</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Yetkiler</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Kayıt Tarihi</th>
                    <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{user.name}</span>
                          {currentUser?.id === user.id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Siz</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{user.email}</td>
                      <td className="px-6 py-4">
                        {getPermissionCount(user) === ALL_PERMISSIONS.length ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Tam Yetki</span>
                        ) : getPermissionCount(user) === 0 ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Yetki Yok</span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                            {getPermissionCount(user)}/{ALL_PERMISSIONS.length} Yetki
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm">{formatDate(user.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {currentUser?.id !== user.id && (
                            <button
                              onClick={() => handleDelete(user.id, user.name)}
                              disabled={deletingId === user.id}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">
                {editingUser ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı Oluştur'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ad Soyad"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ornek@mail.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingUser ? 'Yeni Şifre (boş bırakılırsa değişmez)' : 'Şifre'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    placeholder={editingUser ? 'Değiştirmek için girin' : 'Min 6 karakter'}
                    required={!editingUser}
                    minLength={formData.password ? 6 : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Permissions Section */}
              <div className="border rounded-md">
                <button
                  type="button"
                  onClick={() => setShowPermissions(!showPermissions)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <span>
                    Yetkiler
                    <span className="ml-2 text-xs text-gray-500">
                      ({formData.permissions.length}/{ALL_PERMISSIONS.length} seçili)
                    </span>
                  </span>
                  {showPermissions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showPermissions && (
                  <div className="px-4 pb-4 border-t">
                    <div className="pt-3 pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.permissions.length === ALL_PERMISSIONS.length}
                          onChange={toggleAllPermissions}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">Tümünü Seç / Kaldır</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {ALL_PERMISSIONS.map((perm) => (
                        <label key={perm.key} className="flex items-center gap-2 cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm.key)}
                            onChange={() => togglePermission(perm.key)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Kaydediliyor...' : (editingUser ? 'Güncelle' : 'Oluştur')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
